import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { referralsService } from '@/services/referralsService'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { INDICADOR_TIPOS, indicadorTipoKind, type IndicadorTipo } from './indicadorTipos'

const EMPTY = { contato: '', empresa: '', telefone: '', email: '', cnpj: '', site: '' }

export function IndicadorCadastroModal({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const [tipo, setTipo] = useState<IndicadorTipo>('AGENCIA')
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const empresa = indicadorTipoKind(tipo) === 'empresa'

  const set = (key: keyof typeof EMPTY, value: string) => setForm((prev) => ({ ...prev, [key]: value }))

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setSaving(true)
    setError('')
    try {
      await referralsService.invite({
        tipo,
        contato: form.contato,
        empresa: form.empresa,
        telefone: form.telefone,
        email: form.email,
        cnpj: empresa ? form.cnpj : undefined,
        site: form.site.trim() || undefined,
      })
      setForm(EMPTY)
      setTipo('AGENCIA')
      onSaved()
      onClose()
    } catch (err: unknown) {
      setError(getFriendlyError(err, 'Não foi possível cadastrar o indicador'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title='Cadastrar indicador' description='O convite de acesso sai por e-mail.' size='md'>
      <form className='space-y-4' onSubmit={submit}>
        <div className='grid grid-cols-2 gap-2' role='radiogroup' aria-label='Tipo de indicador'>
          {INDICADOR_TIPOS.map((item) => (
            <button
              key={item.id}
              type='button'
              role='radio'
              aria-checked={tipo === item.id}
              onClick={() => setTipo(item.id)}
              className={
                tipo === item.id
                  ? 'rounded-lg border border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 px-3 py-2 text-sm font-medium text-[var(--brand-primary)]'
                  : 'rounded-lg border border-[var(--glass-border)] px-3 py-2 text-sm text-[var(--font-color-muted)]'
              }
            >
              {item.label}
            </button>
          ))}
        </div>

        {empresa ? (
          <>
            <Input label='Nome da empresa' value={form.empresa} onChange={(e) => set('empresa', e.target.value)} required />
            <Input label='Nome do contato na empresa' value={form.contato} onChange={(e) => set('contato', e.target.value)} required />
            <Input label='CNPJ' value={form.cnpj} onChange={(e) => set('cnpj', e.target.value)} placeholder='00.000.000/0000-00' />
          </>
        ) : (
          <>
            <Input label='Nome do contato' value={form.contato} onChange={(e) => set('contato', e.target.value)} required />
            <Input label='Empresa em que trabalha' value={form.empresa} onChange={(e) => set('empresa', e.target.value)} required />
          </>
        )}
        <Input label='Telefone' value={form.telefone} onChange={(e) => set('telefone', e.target.value)} required />
        <Input label='E-mail' type='email' value={form.email} onChange={(e) => set('email', e.target.value)} required />
        <Input label='Site' value={form.site} onChange={(e) => set('site', e.target.value)} placeholder='https://loja.com.br' />
        {error && <p className='text-sm text-rose-500'>{error}</p>}
        <div className='flex justify-end gap-2'>
          <Button type='button' variant='ghost' onClick={onClose}>Cancelar</Button>
          <Button type='submit' loading={saving}>Cadastrar</Button>
        </div>
      </form>
    </Modal>
  )
}
